import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid2,
  Stack,
  Icon,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

// Multi-lingual support
import { t } from "i18next";

// Modal types
import {
  IComplianceReportData,
  IComplianceReportModalProps,
} from "./ComplianceReportModalTypes";
import { IField } from "../fieldDetailsModal/FieldDetailsModalTypes";
import { EErrorCodes } from "../../../../components/shared/commonTypes/commonTypes";

// API Manager
import {
  getApi,
  getExportApi,
} from "../../../../services/apiManager/ApiManager";

// Hooks
import { useToaster } from "../../../../hooks/useToasterContext/useToasterContext";

// Components
import Loader from "../../../../components/layout/Loader";
import DynamicForm from "../../../../components/shared/commonDynamicForm/CommonDynamicForm";

// Data formatter
import {
  getEudrValueColor,
  getFormattedApprovalStatus,
  getFormattedComplianceStatus,
  getFormattedFloatValue,
  getFormattedOverlapStatus,
} from "../../../shared/commonDataFormatter/CommonDataFormatter";

// Compliance report modal component
const ComplianceReportModal: React.FC<IComplianceReportModalProps> = ({
  handleClose,
  fieldId,
  entityType,
}) => {
  const [fieldData, setFieldData] = useState<IField | null>(null);
  const { setShowToaster } = useToaster();
  const [fieldComplianceFormData, setFieldComplianceFormData] =
    useState<any>(null);
  const [farmerComplianceFormData, setFarmerComplianceFormData] =
    useState<any>(null);
  const reportCellSx = {
    overflowWrap: "anywhere",
  };
  const reportHeadCellSx = {
    width: "33.33%",
    fontWeight: "bold",
    wordBreak: "break-word",
    whiteSpace: "normal",
  };

  // Manage compliance report data
  const [complianceReportList, setComplianceReportList] = useState<
    IComplianceReportData[]
  >([]);

  // Get field details
  const getFieldDetails = async () => {
    try {
      const apiParams = {
        id: fieldId,
      };
      const response = await getApi("/v1/field/details", apiParams);
      if (response.status && response.data) {
        setFieldData(response.data);

        // Get compliance form with values for field & farmer
        fetchInitialComplienceForm(fieldId ?? "");
        fetchInitialFarmerComplianceForm(response.data.farmerId ?? "");
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
    }
  };

  // Get report details
  const getReportDetails = async () => {
    try {
      const apiParams = {
        entityId: fieldId,
        entityType: entityType,
      };
      const response = await getApi("/v1/compliance/report/view", apiParams);
      if (
        response.status &&
        response.data &&
        response.data?.reportData &&
        response.data.reportData.length
      ) {
        setComplianceReportList(response.data.reportData);
      } else {
        setComplianceReportList([]);
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
    }
  };

  // Get details
  useEffect(() => {
    getFieldDetails();
    getReportDetails();
  }, [fieldId, entityType]);

  // Export compliance report
  const exportComplianceReport = async (fieldId: string) => {
    const apiParams = {
      fieldId: fieldId,
    };
    await getExportApi("/v1/compliance/export/field/ddr", apiParams);
  };

  // Export Questionnaire
  const exportQuestionnaire = async (fieldId: string) => {
    const apiParams = {
      field_id: fieldId,
    };
    await getExportApi(
      "/v1/questionnaire/export/responses/farmer-field",
      apiParams,
    );
  };

  // Display value
  const getDisplayValue = (value: any, defaultValue = "-") => {
    return value && value.length ? value : defaultValue;
  };

  // Fetch farmer details
  const fetchInitialFarmerComplianceForm = async (
    farmerComplianceId?: string,
  ) => {
    try {
      const response = !farmerComplianceId
        ? await getApi(`v1/questionnaire/form/FARMER_Q`)
        : await getApi(
            `v1/questionnaire/response?entityId=${farmerComplianceId}&formCategory=FARMER_Q`,
          );
      if (response.status) {
        const farmerDetails = response.data;

        if (farmerDetails) {
          setFarmerComplianceFormData(farmerDetails);
        }
      } else {
        if (response.code === ("E_FORM_RESPONSE_NOT_FOUND" as any)) {
          fetchInitialFarmerComplianceForm();
        }
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
    }
  };

  // Fetch field details
  const fetchInitialComplienceForm = async (fieldIdCompliance?: string) => {
    try {
      const response = !fieldIdCompliance
        ? await getApi(`v1/questionnaire/form/FIELD_Q`)
        : await getApi(
            `v1/questionnaire/response?entityId=${fieldIdCompliance}&formCategory=FIELD_Q`,
          );
      if (response.status) {
        const fieldDetails = response.data;

        if (fieldDetails) {
          setFieldComplianceFormData(fieldDetails);
        }
      } else {
        if (response.code === EErrorCodes.E_FORM_RESPONSE_NOT_FOUND) {
          fetchInitialComplienceForm();
        }
      }
    } catch (error) {
      setShowToaster(true, t("commonMessages.internalServerError"), "error");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Title */}
      <Box
        sx={{
          backgroundColor: "agricolors.drawerheader",
          display: "flex",
          alignItems: "center",
          height: "64px",
          justifyContent: "space-between",
          px: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: "risinblack.main",
            fontWeight: "600",
            fontSize: "18px",
            lineHeight: "24px",
          }}
        >
          {t("fieldDetails.complianceReport.complianceReport")}
        </Typography>
      </Box>

      {/* Content Area */}
      {!fieldData && <Loader />}
      {fieldData && (
        <Box
          sx={{
            flexGrow: 1,
            px: 3,
            py: 2,
            overflowY: "auto",
          }}
        >
          <Box>
            <Box>
              {/* Title */}
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                {fieldData.name}
              </Typography>

              {/* Basic Info */}
              <Grid2 container spacing={2} mb={2}>
                <Grid2 size={3}>
                  <Typography fontSize={13}>
                    {t("fieldDetails.fieldArea")}
                  </Typography>
                  <Typography fontSize={15}>
                    {getFormattedFloatValue(fieldData.fieldArea)}
                  </Typography>
                </Grid2>
                <Grid2 size={3}>
                  <Typography fontSize={13}>
                    {t("fieldDetails.landType")}
                  </Typography>
                  <Typography fontSize={15}>{fieldData.landName}</Typography>
                </Grid2>
                <Grid2 size={2}>
                  <Typography fontSize={13}>
                    {t("fieldDetails.farmerId")}
                  </Typography>
                  <Typography fontSize={15}>{fieldData.farmerSeqId}</Typography>
                </Grid2>

                <Grid2 size={4}>
                  <Typography fontSize={13}>
                    {t(
                      "fieldDetails.complianceReport.fieldIdentificationNumber",
                    )}
                  </Typography>
                  <Typography fontSize={15}>
                    {getDisplayValue(fieldData.fieldIdentificationNumber)}
                  </Typography>
                </Grid2>
              </Grid2>

              {/* Dealer + Vendor details block removed from the
                  Compliance Report on the AA-1569 web pass — the
                  report now focuses purely on the field-level
                  compliance verdict. Tier-1 Supplier ID is still
                  surfaced inside the report body (when present) via
                  the backend EUDR rendering; we just don't repeat it
                  in this modal's header. */}

              <Grid2 container spacing={2} mb={2}>
                <Grid2 size={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" sx={{ fontSize: "12px" }}>
                      {t("fieldDetails.approvalStatus")}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {getFormattedApprovalStatus({
                        status: fieldData.approvalStatus as any,
                      })}
                    </Stack>
                  </Stack>
                </Grid2>

                <Grid2 size={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" sx={{ fontSize: "12px" }}>
                      {t("fieldDetails.overlapStatus")}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {getFormattedOverlapStatus({
                        status: fieldData.overlapStatus as any,
                      })}
                    </Stack>
                  </Stack>
                </Grid2>

                <Grid2 size={4}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" sx={{ fontSize: "12px" }}>
                      {t("fieldDetails.complianceStatus")}
                    </Typography>
                    <Stack direction="row" spacing={1} marginBottom={2}>
                      {getFormattedComplianceStatus({
                        status: fieldData.complianceStatus as any,
                      })}
                    </Stack>
                  </Stack>
                </Grid2>
              </Grid2>
            </Box>

            {/* Table-like Section */}
            <TableContainer
              sx={{ mt: 4, border: "1px solid #ccc" }}
              className="tablestyle"
            >
              <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={reportHeadCellSx}>
                      {t("fieldDetails.complianceReport.category")}
                    </TableCell>
                    <TableCell sx={reportHeadCellSx}>
                      {t("fieldDetails.complianceReport.topic")}
                    </TableCell>
                    <TableCell sx={reportHeadCellSx}>
                      {t("fieldDetails.complianceReport.data")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {complianceReportList.length > 0 ? (
                    complianceReportList.map(
                      (reportData: IComplianceReportData, index: number) => (
                        <TableRow key={index}>
                          <TableCell sx={reportCellSx}>
                            <Typography fontSize={14}>
                              {reportData.category || "-"}
                            </Typography>
                          </TableCell>
                          {/* R14 — colours apply to the result VALUE only.
                              The topic (field name) is no longer reddened for
                              fail-cause rows, so the Topic column stays default
                              and matches the blue behaviour (value-only). */}
                          <TableCell sx={reportCellSx}>
                            <Typography fontSize={14}>
                              {reportData.topic || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={reportCellSx}
                            className={
                              reportData.dataIsFailCause && reportData.data
                                ? "cell-highlight"
                                : ""
                            }
                          >
                            {/* R14 — colour the EUDR result value per the
                                text-colour mapping. Fail-cause rows are already
                                red via cell-highlight; this adds d1/d3 red and
                                the d3/d5 blue multi-option values. */}
                            <Typography
                              fontSize={14}
                              sx={{
                                color: getEudrValueColor(reportData.data),
                              }}
                            >
                              {reportData.data || "-"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography fontSize={14}>
                          {t("fieldDetails.complianceReport.noRecords")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Field Compliance Form */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              {t("fieldDetails.complianceReport.fieldComplianceData")}
            </Typography>

            {fieldComplianceFormData ? (
              <DynamicForm
                formData={fieldComplianceFormData as any}
                onSubmit={(values) => {
                  /* eslint-disable no-empty-function */
                }}
                isHideSubmitBtn={true}
                sxControlStyle={{ width: "100%" }}
                isDisableAllFields={true}
              />
            ) : (
              <Typography
                variant="body1"
                sx={{ color: "gray", textAlign: "center" }}
              >
                {t("commonMessages.noDataFound")}
              </Typography>
            )}
          </Box>

          {/* Farmer Compliance Form */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              {t("fieldDetails.complianceReport.farmerComplianceData")}
            </Typography>
            {farmerComplianceFormData ? (
              <DynamicForm
                formData={farmerComplianceFormData as any}
                onSubmit={(values) => {
                  /* eslint-disable no-empty-function */
                }}
                isHideSubmitBtn={true}
                sxControlStyle={{ width: "100%" }}
                isDisableAllFields={true}
              />
            ) : (
              <Typography
                variant="body1"
                sx={{ color: "gray", textAlign: "center" }}
              >
                {t("commonMessages.noDataFound")}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {/* Footer Buttons */}
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        p={2}
        borderTop="1px solid #e0e0e0"
        sx={{ backgroundColor: "agricolors.drawerfooter", flexShrink: 0 }}
      >
        <Button
          variant="contained"
          sx={{
            backgroundColor: "agricolors.whitebg",
            color: "black",
            border: 1,
            borderColor: "risinblack.main",
            "&:hover": {
              backgroundColor: "#f0f0f0",
            },
            boxShadow: "none",
          }}
          onClick={handleClose}
        >
          {t("fieldDetails.complianceReport.cancel")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          startIcon={<Icon className="icon-download"></Icon>}
          onClick={(event) => {
            exportComplianceReport(fieldId);
          }}
        >
          {t("fieldDetails.complianceReport.downloadDueDiligenceReport")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          startIcon={<Icon className="icon-download"></Icon>}
          onClick={(event) => {
            exportQuestionnaire(fieldId);
          }}
        >
          {t("fieldDetails.complianceReport.exportQuestionnaire")}
        </Button>
      </Box>
    </Box>
  );
};

export default ComplianceReportModal;
